import React, {
  useEffect,
  useRef,
  FunctionComponent,
  KeyboardEvent,
  LiHTMLAttributes,
  SyntheticEvent,
} from "react";
import classnames from 'classnames';

import { keyPressHelper } from '@helpers/accessibility';
import { trimProps } from '@components/library/helper';

interface DropdownItemProps {
  styleClassNames: StyleClassNames;
  variantName: string;
  name: Name;
  icon?: string;
  iconSize?: number;
  isArrowed?: boolean;
  isDisabled?: boolean;
  isDisplayed?: boolean;
  isSelected?: boolean;
  isTabbable?: boolean;
  isWarningItem?: boolean;
  onChoose?: (name: Name, e?: SyntheticEvent) => void;
}

type StyleClassNames = { [key: string]: string };
type Name = string | number;

// all props non-native to the element so they can be stripped away after use
const nonNativeProps = 'icon,iconSize,isArrowed,isDisabled,isDisplayed,isSelected,isTabbable,isWarningItem,onChoose,size,styleClassNames,variantName';

const baseName = 'dropdown-item';

const BaseDropdownItem: FunctionComponent<DropdownItemProps & LiHTMLAttributes<HTMLLIElement>> = (
  props
) => {
  const {
    children,
    className,
    icon = '',
    iconSize,
    isArrowed,
    isDisabled = false,
    isDisplayed = true,
    isSelected,
    isTabbable = true,
    isWarningItem = false,
    onChoose,
    styleClassNames = {}, // default value for testing (required prop)
    name,
    variantName = '', // default value for testing (required prop)
  } = props;
  const itemRef = useRef(null);

  const observerCallback = (entries: any): void => {
    if (isArrowed && itemRef.current && entries[0].intersectionRatio < 0.90) {
      // if less than 90% of item is showing, it will scroll to display the item
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  };

  useEffect(() => {
    if (itemRef.current) {
      const observer = new IntersectionObserver(observerCallback);
      observer.observe(itemRef.current);
      return (): void => { observer.disconnect(); };
    }
    return undefined;
  }, [isArrowed, itemRef]);

  const onKeyPressHandler = keyPressHelper(onChoose);

  return isDisplayed && (
    <li
      {...trimProps(props, nonNativeProps)}
      className={classnames(
        `bf-${baseName}__${variantName}`, // base-variant class for post consumption styling
        styleClassNames[variantName], // class for css modules
        className, // user specified class
        { [styleClassNames[`${variantName}-warning`]]: isWarningItem },
        { [styleClassNames.selected]: isSelected },
        { [styleClassNames.arrowed]: isArrowed },
        { [styleClassNames['not-selectable']]: isDisabled },
      )}
      onClick={(e: SyntheticEvent): void => onChoose(name, e)}
      onKeyPress={(e: KeyboardEvent<HTMLLIElement>): void => onKeyPressHandler(e, name)}
      ref={itemRef}
      role="button"
      tabIndex={isTabbable ? 0 : undefined}
    >
      {icon && (
        <span
          className={`${icon} ${styleClassNames.icon}`}
          style={iconSize && { fontSize: `${iconSize}px` }}
        />
      )}
      {children}
    </li>
  );
};

export default BaseDropdownItem;
